import { ArrowForwardIos } from '@interhyp/platform-icons';
import classnames from 'classnames';
import { addDays, addMonths, addWeeks, eachDayOfInterval, eachWeekOfInterval, format, getDaysInMonth, isWithinInterval, parse, subMonths, isSameMonth, isToday } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Locales } from "../../../../helper/Locales.js";
import styles from "./DatepickerCalendar.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const isDayInMonth = (day, month) => {
  const daysInMonth = getDaysInMonth(month);
  return isWithinInterval(day, {
    start: month,
    end: addDays(month, daysInMonth - 1)
  });
};
const minimumAmountOfWeeks = 6;
const getWeeks = month => {
  const daysInMonth = getDaysInMonth(month);
  const weeks = eachWeekOfInterval({
    start: month,
    end: addDays(month, daysInMonth - 1)
  }, {
    weekStartsOn: 1
  });
  while (weeks.length < minimumAmountOfWeeks) {
    weeks.push(addWeeks(weeks[weeks.length - 1], 1));
  }
  return weeks.map(week => {
    return eachDayOfInterval({
      start: week,
      end: addDays(week, 6)
    });
  });
};
export const DatepickerCalender = ({
  locale,
  value,
  onSelection,
  isDateDisabled,
  initialMonth
}) => {
  const dateFormat = Locales[locale];
  const weekDays = dateFormat.date.days;
  const [month, setMonth] = useState(new Date());
  const getDateForCurrentMonth = useCallback(() => {
    let today = new Date();
    if (value) {
      const parseDate = parse(value, dateFormat.date.dateFormat, new Date());
      if (!Number.isNaN(parseDate.getTime())) {
        today = parseDate;
      }
    } else if (initialMonth) {
      return initialMonth;
    }
    return new Date(today.getFullYear(), today.getMonth());
  }, [value, initialMonth, dateFormat.date.dateFormat]);
  const nextMonth = useCallback(() => {
    setMonth(addMonths(month, 1));
  }, [month]);
  const previousMonth = useCallback(() => {
    setMonth(subMonths(month, 1));
  }, [month]);
  const isSelected = useCallback(day => {
    if (value) {
      const selectedDay = parse(value, dateFormat.date.dateFormat, new Date());
      return day.getTime() === selectedDay.getTime();
    }
    return false;
  }, [dateFormat, value]);
  const handleSelection = useCallback(day => {
    if (!isDateDisabled?.(day)) {
      onSelection?.(day, format(day, dateFormat.date.dateFormat));
    }
  }, [dateFormat.date.dateFormat, isDateDisabled, onSelection]);

  /**
   * Return the selected day as headline or the month if no day was selected
   */
  const getHeadline = useCallback(() => {
    if (value) {
      const selectedDay = parse(value, dateFormat.date.dateFormat, new Date());
      if (isSameMonth(selectedDay, month)) {
        return format(selectedDay, 'dd. MMMM yyyy', {
          locale: deLocale
        });
      }
    }
    return format(month, 'MMMM yyyy', {
      locale: deLocale
    });
  }, [value, dateFormat, month]);

  /**
   * Set local month when getDateForCurrentMonth changes -> initial, on value or dateformat change
   */
  useEffect(() => {
    setMonth(getDateForCurrentMonth());
  }, [getDateForCurrentMonth]);
  const weeksOfMonth = useMemo(() => getWeeks(month), [month]);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.datepickerCalendar,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.header,
      children: [/*#__PURE__*/_jsx("div", {
        onClick: previousMonth,
        className: styles.left,
        children: /*#__PURE__*/_jsx(ArrowForwardIos, {
          className: styles.leftArrow
        })
      }), /*#__PURE__*/_jsx("div", {
        className: styles.date,
        children: getHeadline()
      }), /*#__PURE__*/_jsx("div", {
        onClick: nextMonth,
        className: styles.right,
        children: /*#__PURE__*/_jsx(ArrowForwardIos, {})
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.days,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.week,
        children: weekDays.map(day => /*#__PURE__*/_jsx("div", {
          className: styles.day,
          children: day
        }, day))
      }), weeksOfMonth.map((week, index) => /*#__PURE__*/_jsx("div", {
        className: styles.week,
        children: week.map((day, index) => {
          const classes = classnames(styles.day, {
            [styles.overlap]: !isDayInMonth(day, month),
            [styles.active]: isSelected(day),
            [styles.today]: isToday(day),
            [styles.selectable]: !isDateDisabled?.(day)
          });
          return /*#__PURE__*/_jsx("div", {
            onClick: () => handleSelection(day),
            className: classes,
            children: day.getDate()
          }, index);
        })
      }, index))]
    })]
  });
};